import {getExtensionFromMimeType, MimeType} from '@shopify/mime-types';

import {MediaValidationResult, MediaValidationStatus} from './types';

// Contact PSN before updating maxMegapixels & maxFileSizeInMegabytes
const maxMegapixels = 20;
const maxFileSizeInMegabytes = 20;

const unsupportedByBrowserImageTypes = ['image/heic'];

export const ImageDefaults = {
  maxMegapixels,
  maxFileSizeInMegabytes,
  maxFileSizeInBytes: maxFileSizeInMegabytes * 1000 * 1000,
  validExtensions: ['.jpg', '.gif', '.png', '.webp', '.heic'],
  validMimeTypes: [
    MimeType.Png,
    MimeType.Jpeg,
    MimeType.Gif,
    MimeType.Webp,
    MimeType.Heic,
  ],
};

export const ValidDropzoneImages = ImageDefaults.validMimeTypes.join(',');

export interface ValidateImageOptions {
  maxFileSize: number;
  maxResolutionInMegapixels: number;
  validMimeTypes: MimeType[];
}

export function validateImage(
  image: File,
  {
    maxFileSize,
    maxResolutionInMegapixels,
    validMimeTypes,
  }: ValidateImageOptions,
): Promise<MediaValidationResult> {
  if (!validMimeTypes.some((type) => type.toString() === image.type)) {
    return Promise.resolve({
      status: MediaValidationStatus.InvalidImageFileType,
      extensions: validMimeTypes.map(getExtensionFromMimeType),
    });
  }

  if (image.size > maxFileSize) {
    return Promise.resolve({
      status: MediaValidationStatus.ExceedsMaximumSize,
      maximumSize: maxFileSize,
    });
  }

  // The file reader can't validate dimensions for images that are not supported
  //   by the browser, such image/heic. We will rely on the backend to do this
  //   validation.
  if (unsupportedByBrowserImageTypes.includes(image.type)) {
    return Promise.resolve({status: MediaValidationStatus.Valid});
  }
  return validateDimensions(image, maxResolutionInMegapixels, validMimeTypes);
}

function validateDimensions(
  image: File,
  maxResolutionInMegapixels: number,
  validMimeTypes: MimeType[],
): Promise<MediaValidationResult> {
  const maxResolution = maxResolutionInMegapixels * 1000 * 1000;

  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event: Event) => {
      const imageElement = new Image();

      imageElement.onload = (onloadEvent: Event) => {
        const {target} = onloadEvent;
        const {width, height} = target as HTMLImageElement;
        const pixels = width * height;

        if (pixels > maxResolution) {
          return resolve({
            status: MediaValidationStatus.ExceedsMaximumDimensions,
            maxMegapixels: maxResolutionInMegapixels,
          });
        } else if (pixels <= 0 || isNaN(pixels)) {
          return resolve({
            status: MediaValidationStatus.CorruptedFile,
            extensions: validMimeTypes.map(getExtensionFromMimeType),
          });
        }

        resolve({status: MediaValidationStatus.Valid});
      };

      imageElement.onerror = () => {
        resolve({
          status: MediaValidationStatus.CorruptedFile,
          extensions: validMimeTypes.map(getExtensionFromMimeType),
        });
      };

      // Casting to `any` below since no matching type found
      // for event which has a `result` property on it
      imageElement.src = (event.target as any).result;
    };

    reader.readAsDataURL(image);
  });
}
